import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
}

const GA_MEASUREMENT_ID = 'G-G2KMYSEDB3'

// Load the Google Analytics script immediately for tag detection
// This allows Google to detect the tag, but we only send data after consent
function loadGAScript() {
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    return // Already loaded
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  // Initialize gtag
  window.dataLayer = window.dataLayer || []
  window.gtag = function () {
    window.dataLayer?.push(arguments)
  }
  window.gtag('js', new Date())

  // Set default consent to denied (Google Consent Mode v2)
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  })

  // Configure but don't send pageviews yet (wait for consent)
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
  })
}

// Load script immediately when module is imported
loadGAScript()

// Google Consent Mode v2: Grant consent
export function grantConsent() {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied', // Still deny ad storage for privacy
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    })

    // Initialize analytics after consent
    initializeAnalytics()
  }
}

// Google Consent Mode v2: Deny consent
export function denyConsent() {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    })
  }
}

export function useAnalytics() {
  const location = useLocation()

  useEffect(() => {
    // Check if user has consented to analytics
    const hasConsent = localStorage.getItem('analytics_consent') === 'true'

    // Respect Do Not Track browser setting
    const doNotTrack =
      navigator.doNotTrack === '1' ||
      (window as any).doNotTrack === '1' ||
      (navigator as any).msDoNotTrack === '1'

    if (!hasConsent || doNotTrack) {
      return
    }

    // Track page view only after consent
    if (window.gtag) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
        anonymize_ip: true,
      })
    }
  }, [location])

  // Memoize trackEvent function to prevent unnecessary re-renders
  const trackEvent = useCallback(
    (eventName: string, eventParams?: Record<string, any>) => {
      const hasConsent = localStorage.getItem('analytics_consent') === 'true'
      const doNotTrack =
        navigator.doNotTrack === '1' ||
        (window as any).doNotTrack === '1' ||
        (navigator as any).msDoNotTrack === '1'

      if (!hasConsent || doNotTrack || !window.gtag) {
        return
      }

      window.gtag('event', eventName, {
        ...eventParams,
        anonymize_ip: true,
      })
    },
    [],
  ) // Empty dependency array means this function is stable across renders

  return {
    trackEvent,
  }
}

export function initializeAnalytics() {
  const hasConsent = localStorage.getItem('analytics_consent') === 'true'
  const doNotTrack =
    navigator.doNotTrack === '1' ||
    (window as any).doNotTrack === '1' ||
    (navigator as any).msDoNotTrack === '1'

  if (!hasConsent || doNotTrack) {
    return
  }

  // Script is already loaded, just enable tracking
  if (window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
    })
  }
}

export function revokeAnalyticsConsent() {
  localStorage.setItem('analytics_consent', 'false')

  // Update consent mode to denied
  denyConsent()

  // Clear Google Analytics cookies
  const cookies = document.cookie.split(';')
  for (let cookie of cookies) {
    const [name] = cookie.split('=')
    if (name.trim().startsWith('_ga')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    }
  }

  // Reload to stop tracking
  window.location.reload()
}
